import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useGetRecoveryConfigQuery } from 'api/recoveryAi'
import type { PhilosophyPosture, RecoveryConfig } from 'api/recoveryAi.types'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import GenerositySliderBody from 'scenes/RecoveryAI/components/GenerositySliderBody'
import SettingsPaneChrome from 'scenes/RecoveryAI/components/SettingsPaneChrome'
import { DEFAULT_RECOMMENDED_POSTURE } from 'scenes/RecoveryAI/components/stances'
import { successNotificationOverrides } from 'scenes/RecoveryAI/components/successNotificationOverrides'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import { useSaveAndTest } from './useSaveAndTest'
import { useUnsavedChangesPrompt } from './useUnsavedChangesPrompt'

const t = buildTranslate('recovery_ai.wizard.philosophy')
const tSettings = buildTranslate('recovery_ai.settings.recovery_generosity')

// Horizontal gap between the two footer buttons (mirrors BrandVoicePane).
const SAVE_FOOTER_GAP = '16px'

type Props = {
  // Still passed by Settings/index.tsx; unused since AI-1170 removed
  // Save-and-test. Drop with the parent in a follow-up.
  isActivated: boolean
}

// Settings → Recovery generosity pane (AI-29, re-platformed for R12 / AI-62).
// Config-backed sibling of the wizard's Recovery Philosophy step. R12 replaces
// the 4-card StanceCardList with the GenerositySliderBody and the AI-29
// debounced auto-save-on-select with the explicit staged Save model (shared via
// useSaveAndTest, same as Brand voice / Escalation rules).
// AI-1166: the "Recommended for you" badge follows the merchant's actual
// quiz result (`config.philosophy_recommended_posture`), falling back to
// DEFAULT_RECOMMENDED_POSTURE only when the config has none (e.g. a merchant
// onboarded before the recommendation was persisted).
// AI-1170: Save-and-test is removed everywhere — testing happens on the
// separate Agent Tuning page instead.
const RecoveryGenerosityPane = (_props: Props) => {
  const [css] = useStyletron()
  // AI-64: admin-only writes. A view-only operator (RBAC can-view, not
  // view-and-edit) can reach this route, so every edit affordance — the slider
  // and both persist buttons — goes disabled. Mirrors the CASL gate
  // EscalationRulesPane / DailyBudgetPane already apply.
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')
  // `isError` (the GET) surfaces a failed config load so we never present the
  // 'generous' fallback as if it were the merchant's saved setting.
  const {
    data: config,
    isLoading,
    isError: isLoadError,
  } = useGetRecoveryConfigQuery()

  // Local echo of the merchant's chosen posture. Seeded once from the saved
  // config; the PUT fires only on the explicit Save / Save-and-test buttons.
  const [posture, setPosture] = useState<PhilosophyPosture>(
    DEFAULT_RECOMMENDED_POSTURE
  )

  const buildPayload = useCallback(
    (): Partial<RecoveryConfig> => ({ philosophy_posture: posture }),
    [posture]
  )

  // Dirty when the chosen posture differs from the saved config (collapsing a
  // missing saved posture to the same default the UI shows). A pristine form is
  // non-dirty, so Save can't fire a no-op PUT.
  const isDirty = useMemo(() => {
    if (!config) return false
    return (
      posture !== (config.philosophy_posture ?? DEFAULT_RECOMMENDED_POSTURE)
    )
  }, [config, posture])

  // Save / Save-and-test orchestration (mutation, success flag, error/loading
  // flags, dirty-gating + canSave, post-save navigation + unsaved-guard bypass)
  // shared with the other RecoveryAI Settings panes.
  const {
    handleSave,
    clearSuccess,
    saveSuccess,
    isSaving,
    isSaveError,
    canSave,
    guardBypassRef,
  } = useSaveAndTest(buildPayload, isDirty)

  // Warn before leaving the route (in-app nav + refresh/close) while dirty;
  // guardBypassRef lets Save-and-test's own post-save navigation through (AI-174).
  const unsavedChangesPrompt = useUnsavedChangesPrompt(isDirty, guardBypassRef)

  // Hydrate local state ONCE on first config load (RTK Query hands back a new
  // `config` reference on every background refetch; the ref guards against
  // re-hydration clobbering an in-progress selection).
  const hasHydrated = useRef(false)
  useEffect(() => {
    if (!config || hasHydrated.current) return
    hasHydrated.current = true
    setPosture(config.philosophy_posture ?? DEFAULT_RECOMMENDED_POSTURE)
  }, [config])

  // Selecting a stance only updates local state (+ drops a stale "Saved"
  // banner); the PUT fires on Save / Save-and-test.
  const handleSelect = useCallback(
    (next: PhilosophyPosture) => {
      setPosture(next)
      clearSuccess()
    },
    [clearSuccess]
  )

  if (isLoading) {
    // The shared Spinner renders no role/aria text, so wrap it in a live status
    // region with an i18n accessible name (WCAG 4.1.3) — mirrors the sibling
    // panes (Approved rewards / Escalation rules).
    return (
      <div role="status" aria-label={tSettings('loading')}>
        <Spinner isLoading size="4x" className="text-center margin-top-huge" />
      </div>
    )
  }

  // A failed config fetch (or a payload-less resolve) surfaces an error — never
  // the default-posture slider as if it loaded — so a broken load can't be
  // mistaken for a saved 'generous' setting, and a Save can't overwrite the real
  // saved posture from a broken state.
  if (isLoadError || config == null) {
    return (
      <div className="p-l">
        <Notification kind="negative" size="full">
          {tSettings('load_error')}
        </Notification>
      </div>
    )
  }

  return (
    <SettingsPaneChrome title={tSettings('title')} subtitle={t('description')}>
      {/* Guard whole-route navigation (in-app + refresh/close) while dirty. */}
      {unsavedChangesPrompt}

      {/* Save failure — inline, tracks the mutation's isError flag. */}
      {isSaveError && (
        <div className="mb-m">
          <Notification kind="negative" size="full">
            {tSettings('save_error')}
          </Notification>
        </div>
      )}

      {/* Save success — POLITE status region (does not interrupt). */}
      {saveSuccess && !isSaveError && (
        <div className="mb-m">
          <Notification
            kind="positive"
            size="full"
            overrides={successNotificationOverrides}
          >
            {tSettings('save_success')}
          </Notification>
        </div>
      )}

      {/* AI-917: explains the "Recommended for you" badge below — Settings-only,
          per the Figma (the Agent-Tuning modal deliberately omits this line). */}
      <Text.BodyText3 color="grey70" className="mb-l">
        {tSettings('recommendation_basis')}
      </Text.BodyText3>

      <GenerositySliderBody
        value={posture}
        onSelect={handleSelect}
        disabled={!canManage}
        recommendedPosture={
          config.philosophy_recommended_posture ?? DEFAULT_RECOMMENDED_POSTURE
        }
      />

      <div className={`d-flex mt-xl ${css({ gap: SAVE_FOOTER_GAP })}`}>
        {/* AI-174: Save is REALLY disabled when there's nothing to persist; it
            disables in flight. AI-64: also disables outright for a view-only
            operator. AI-1170: Save-and-test is removed everywhere — testing
            happens on the separate Agent Tuning page instead. */}
        <Button
          kind="primary"
          onClick={handleSave}
          disabled={!canManage || !canSave}
          isLoading={isSaving}
        >
          {tSettings('save')}
        </Button>
      </div>
    </SettingsPaneChrome>
  )
}

export default RecoveryGenerosityPane
